import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

// Import layouts (eager — needed for first paint)
import MainLayout from '../components/layout/MainLayout';
import DashboardLayout from '../components/layout/DashboardLayout';

// Import small/critical pages (eager — needed for first paint)
import Home from '../pages/Home';
import Login from '../pages/Login';
import Signup from '../pages/Signup';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import NotFound from '../pages/NotFound';

// Import navigation guard (eager — lightweight wrapper)
import { NavigationGuard } from '../navigation/NavigationGuard';

// Lazy-loaded pages (heavy components, split into separate chunks)
const ServicesList = React.lazy(() => import('../pages/ServicesList'));
const SearchResults = React.lazy(() => import('../pages/SearchResults'));
const ContractorsList = React.lazy(() => import('../pages/ContractorsList'));
const ContractorDetails = React.lazy(() => import('../pages/ContractorDetails'));
const BookingPage = React.lazy(() => import('../pages/BookingPage'));

// Lazy-loaded dashboards (largest bundles)
const ContractorDashboard = React.lazy(() => import('../components/dashboard/ContractorDashboard'));
const ClientDashboard = React.lazy(() => import('../components/dashboard/ClientDashboard'));
const AdminDashboard = React.lazy(() => import('../components/dashboard/AdminDashboard'));

// Lazy-loaded standalone pages
const EstimatePage = React.lazy(() => import('../pages/EstimatePage'));

// Routes component
const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><div>Loading...</div></div>}>
    <Routes>
      {/* Public routes with MainLayout */}
      <Route element={<MainLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/services" element={<ServicesList />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/contractors/:serviceId" element={<ContractorsList />} />
        <Route path="/contractor/:contractorId" element={<ContractorDetails />} />
      </Route>

      {/* Custom booking page (standalone, no layout - has its own branding) */}
      <Route path="/book/:slug" element={<BookingPage />} />

      {/* Public estimate page (standalone, no auth required) */}
      <Route path="/estimate/:token" element={<EstimatePage />} />

      {/* Password reset pages (standalone, no layout) */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Protected dashboard routes with DashboardLayout */}
      <Route element={<DashboardLayout />}>
        <Route
          path="/contractor-dashboard/*"
          element={
            <NavigationGuard requiresAuth={true} allowedUserTypes={['contractor']}>
              <ContractorDashboard />
            </NavigationGuard>
          }
        />
        <Route
          path="/client-dashboard/*"
          element={
            <NavigationGuard requiresAuth={true} allowedUserTypes={['client']}>
              <ClientDashboard />
            </NavigationGuard>
          }
        />
      </Route>

      {/* Admin route - standalone without layout (has its own authentication) */}
      {/* TODO: Add proper backend authentication check for admin access */}
      <Route path="/admin" element={<AdminDashboard />} />

      {/* 404 route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
};

// Wrap routes in BrowserRouter
const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
};

export default AppRouter;